import type { CreditMemoStatus, CreditMemoType } from '@/lib/api/types/billing';
import type { StoredAggregatedTaxBreakdownRow } from '@/lib/tax/tax-utils';

export type { CreditMemoStatus, CreditMemoType };

export interface CreditMemo {
  id: string;
  tenantId: string;
  creditMemoNumber: string;
  type: CreditMemoType;
  status: CreditMemoStatus | string;
  contactId?: string | null;
  invoiceId?: string | null;
  customer?: { contactId?: string; displayName?: string } | null;
  lineItems: CreditMemoLineItem[];
  subtotal: number;
  taxTotal: number;
  total: number;
  remainingCredit: number;
  txnDate?: string | null;
  privateNote?: string | null;
  customerMemo?: string | null;
  source?: string;
  sourceType?: string | null;
  sourceId?: string | null;
  qbCreditMemoId?: string | null;
  qbRefundReceiptId?: string | null;
  qbSyncedAt?: string | null;
  accountingSync?: CreditMemoAccountingSync | null;
  /**
   * PR #591 / #592 follow-up: per-jurisdiction tax breakdown copied from
   * the originating invoice at credit-memo creation time. Null on legacy
   * rows and on memos created without a linked invoice — the detail page
   * falls back to the rolled-up `taxTotal` row in that case.
   */
  taxSnapshot?: StoredAggregatedTaxBreakdownRow[] | null;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface CreditMemoApplication {
  invoiceId: string;
  invoiceNumber?: string;
  amountApplied: number;
  appliedAt?: string;
  reversedAt?: string;
  reversalReason?: string;
}

export interface CreditMemoAccountingSync extends Record<string, unknown> {
  applications?: unknown[];
}

export function getCreditMemoApplications(creditMemo: CreditMemo): CreditMemoApplication[] {
  const applications = creditMemo.accountingSync?.applications;
  if (!Array.isArray(applications)) return [];
  return applications.flatMap((value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
    const row = value as Record<string, unknown>;
    if (typeof row.invoiceId !== 'string' || typeof row.amountApplied !== 'number') return [];
    return [
      {
        invoiceId: row.invoiceId,
        invoiceNumber: typeof row.invoiceNumber === 'string' ? row.invoiceNumber : undefined,
        amountApplied: row.amountApplied,
        appliedAt: typeof row.appliedAt === 'string' ? row.appliedAt : undefined,
        reversedAt: typeof row.reversedAt === 'string' ? row.reversedAt : undefined,
        reversalReason: typeof row.reversalReason === 'string' ? row.reversalReason : undefined,
      },
    ];
  });
}

export interface CreditMemoLineItem {
  name?: string;
  description?: string;
  quantity?: number;
  unitPrice?: number;
  amount?: number;
}

export interface CreateCreditMemoInput {
  type: CreditMemoType;
  reasonCode: string;
  reasonNote?: string;
  lineDescription?: string;
  amount: number;
  contactId?: string;
  invoiceId?: string;
  sourcePaymentId?: string;
  customerMemo?: string;
  privateNote?: string;
  lineItems?: CreditMemoLineItem[];
  applyToInvoiceId?: string;
  applyAmount?: number;
}

export interface ApplyCreditMemoInput {
  invoiceId: string;
  amount: number;
}

export interface CashOutCreditMemoInput {
  reason?: string;
}

export interface VoidCreditMemoInput {
  reason?: string;
}

export interface ApplyCreditMemoResult {
  creditMemoId: string;
  invoiceId: string;
  amountApplied: number;
  remainingCredit: number;
  invoiceAmountDue: number;
  invoiceCreditsApplied: number;
}

export interface AutoSuggestCreditEntry {
  creditMemoId: string;
  creditMemoNumber: string;
  type: CreditMemoType;
  remainingCredit: number;
  suggestedAmount: number;
}

export interface CreditMemoListFilters {
  type?: CreditMemoType;
  status?: string;
  contactId?: string;
  search?: string;
  creditMemoNumber?: string;
  invoiceId?: string;
  dateFrom?: string;
  dateTo?: string;
  totalEq?: number;
  totalMin?: number;
  totalMax?: number;
  remainingEq?: number;
  remainingMin?: number;
  remainingMax?: number;
  sortBy?: 'createdAt' | 'total' | 'remainingCredit' | 'creditMemoNumber';
  sortDir?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface PaginatedCreditMemos {
  data: CreditMemo[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}
